import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Layers,
  ListTree,
  Server,
  Users,
} from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { useUiStore } from "../../stores/uiStore";
import {
  useAdminJobs,
  useMe,
  useOpsManager,
  useSystemCounts,
} from "../../hooks/queries";
import { fieldJobsSummary, fmtEta, isActiveJob } from "../../lib/fieldJobs";
import {
  prereqIdsOf,
  sortJobsForDisplay,
  vmLabel,
  vmObservation,
} from "./opsDashboard";
import {
  CapacityPanel,
  Panel,
  ProcessesPanel,
  Stat,
} from "./SystemDashboardPanels";
import { Modal } from "../../ui/Modal";
import { UsersPanel } from "./SystemDashboardUsers";
import { ApiKeysPanel } from "./ApiKeysPanel";

// Full-screen overlay, not a route: the app has no router in use (work orders set the precedent)
// and an overlay can't be deep-linked into by a non-admin who guesses a URL. THIS COMPONENT IS NOT
// A SECURITY BOUNDARY.

// Two SERVED halves, not a status filter: BE-Light returns {active, recent}. `recent` is terminal jobs
// from the last 24 h, SYSTEM-WIDE — the org-scoped read would show one farm's history on a system view.
const JOB_VIEWS = [
  { key: "active" as const, label: "Active (running + queued)" },
  { key: "recent" as const, label: "Recently finished (24 h)" },
];

function JobsPanel({ enabled }: { enabled: boolean }) {
  const [view, setView] = useState<"active" | "recent">("active");
  const [q, setQ] = useState("");
  const jobs = useAdminJobs(enabled, view);
  const rows = useMemo(
    () => (Array.isArray(jobs.data) ? jobs.data : []),
    [jobs.data],
  );

  const shown = useMemo(() => {
    // ORDER: the active list reads as the QUEUE (running, then position order); a terminal list
    // reads as history (newest completion first).
    const ordered = sortJobsForDisplay(
      rows,
      view === "active" ? "active" : "terminal",
    );
    const needle = q.trim().toLowerCase();
    if (!needle) return ordered;
    // search the ID first (that's what you paste in from a log), then the human fields
    return ordered.filter(
      (j) =>
        j.job_id?.toLowerCase().includes(needle) ||
        (j.process_name ?? j.process_id ?? "").toLowerCase().includes(needle) ||
        (j.field_name ?? "").toLowerCase().includes(needle),
    );
  }, [rows, q, view]);

  return (
    <Panel
      title="Compute jobs"
      icon={<ListTree size={12} />}
      state={{ isLoading: jobs.isLoading, isError: jobs.isError }}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select
          className="rounded border border-line bg-canvas px-1.5 py-1 text-[11px] text-fg"
          value={view}
          onChange={(e) => setView(e.target.value as "active" | "recent")}
          aria-label="Job view"
        >
          {JOB_VIEWS.map((v) => (
            <option key={v.key} value={v.key}>
              {v.label}
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search job ID, process, field…"
          aria-label="Search compute jobs"
          className="min-w-0 flex-1 rounded border border-line bg-canvas px-1.5 py-1 text-[11px] outline-none placeholder:text-faint"
        />
        <span className="text-[10px] text-faint">
          {shown.length} of {rows.length}
        </span>
      </div>

      {shown.length === 0 ? (
        <p className="text-[11px] text-faint">
          {rows.length === 0
            ? view === "active"
              ? "Nothing running or queued."
              : "Nothing finished in the last 24 h."
            : view === "recent"
              ? "Nothing matches that search in the last 24 h — an older job won’t be in this list."
              : "Nothing matches that search."}
        </p>
      ) : (
        <ul className="space-y-1">
          {shown.slice(0, 200).map((j) => (
            <li
              key={j.job_id}
              className="rounded border border-line px-2 py-1 text-[11px]"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-fg">
                  {j.process_name ?? j.process_id}
                </span>
                <span className="text-[10px] text-muted">
                  {j.field_name ?? j.field_id?.slice(0, 8) ?? ""}
                </span>
                <span
                  className={`text-[10px] ${j.status === "failed" ? "text-red-400" : j.overdue ? "text-amber-500" : "text-faint"}`}
                >
                  {j.status}
                  {j.status === "waiting" &&
                    j.queue_position != null &&
                    ` #${j.queue_position}`}
                  {j.overdue && " · overdue"}
                </span>
                {typeof j.attempts === "number" && j.attempts > 1 && (
                  <span className="text-[10px] text-amber-500">
                    attempt {j.attempts}
                  </span>
                )}
              </div>
              {/* the job ID, selectable — the whole point is pasting it into compute's tooling */}
              <div className="mt-0.5 select-all font-mono text-[10px] text-faint">
                {j.job_id}
              </div>
              {/*
 Prereqs answer "why hasn't this started?". BE-Light resolves them server-side (name +
                  status), because a list of job IDs is not an explanation on a dashboard. And the
                  distinction that matters: blocking_count 0 with prereqs present means the DEPENDENCIES
                  ARE DONE and it's queued on capacity — which a naive "waiting on X" would explain wrongly.
*/}
              {(() => {
                const prereqs = j.prereqs ?? [];
                const ids = prereqIdsOf(
                  j as unknown as Record<string, unknown>,
                );
                if (!prereqs.length && !ids.length) return null;
                const blocking =
                  j.blocking_count ??
                  prereqs.filter((p) => p.status !== "complete").length;
                return (
                  <div className="mt-0.5 text-[10px] text-faint">
                    {blocking > 0
                      ? "waiting on: "
                      : "prerequisites done — queued on capacity: "}
                    {prereqs.length ? (
                      prereqs.map((p, n) => (
                        <span key={p.job_id}>
                          {n > 0 && ", "}
                          {p.process_name ?? p.job_id}
                          <span
                            className={
                              p.status === "complete"
                                ? "text-emerald-400"
                                : "text-amber-500"
                            }
                          >
                            {" "}
                            [{p.status ?? "unknown"}]
                          </span>{" "}
                          {/* A TERMINAL prereq can't be in the active list, so the click also switches to the recent view rather than searching a list it can't be in. */}
                          <button
                            type="button"
                            className="select-all font-mono underline decoration-dotted hover:text-fg"
                            aria-label={`Search for ${p.job_id}`}
                            title={`Search for ${p.job_id}`}
                            onClick={() => {
                              if (
                                p.status &&
                                p.status !== "in_progress" &&
                                p.status !== "waiting" &&
                                p.status !== "blocked"
                              ) {
                                setView("recent");
                              }
                              setQ(p.job_id);
                            }}
                          >
                            {p.job_id}
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="select-all font-mono">
                        {ids.join(", ")}
                      </span>
                    )}
                  </div>
                );
              })()}
              {j.completed_at && (
                <div className="mt-0.5 text-[10px] text-faint">
                  finished {j.completed_at.slice(0, 16).replace("T", " ")}
                  {typeof j.actual_seconds === "number" &&
                    ` · took ${fmtEta(j.actual_seconds)}`.replace("~", "")}
                </div>
              )}
              {j.error && (
                <div className="mt-0.5 text-[10px] text-red-400">{j.error}</div>
              )}
            </li>
          ))}
        </ul>
      )}
      {shown.length > 200 && (
        <p className="mt-1 text-[10px] text-faint">
          Showing the first 200 — narrow the search.
        </p>
      )}
    </Panel>
  );
}

export function SystemDashboard() {
  const open = useUiStore((s) => s.systemDashboard);
  const close = useUiStore((s) => s.closeSystemDashboard);
  // /users/me, not the login snapshot -- see MeRecord. Until it resolves we do NOT claim non-admin.
  const session = useAuthStore((s) => s.session);
  const me = useMe(!!session && open);
  const isAdmin = !!me.data?.is_global_admin;
  const enabled = open && isAdmin;

  const counts = useSystemCounts(enabled);
  const manager = useOpsManager(enabled);
  const jobs = useAdminJobs(enabled);

  // belt AND braces: the client normalises these, but a render site must never be the thing that throws
  const jobRows = useMemo(
    () => (Array.isArray(jobs.data) ? jobs.data : []),
    [jobs.data],
  );
  const activeJobs = useMemo(() => jobRows.filter(isActiveJob), [jobRows]);
  const vm = useMemo(
    () => vmObservation(manager.data, Date.now()),
    [manager.data],
  );
  // the 12 projected columns arrive as `rows`; the FE reads the first
  const mgr = manager.data?.rows?.[0];
  // compute's note: `blocked` jobs read as live backlog at a glance but aren't running or queued — a chain
  // whose prereq died sits here. Counting them separately is the cheap half of their "dead chains" idea.
  const blocked = useMemo(
    () => jobRows.filter((j) => j.status === "blocked"),
    [jobRows],
  );
  const jobSummary = useMemo(
    () => fieldJobsSummary(activeJobs, Date.now()),
    [activeJobs],
  );

  if (!open) return null;

  return (
    <Modal>
      <div className="fixed inset-0 z-panel overflow-auto bg-canvas">
        <header className="sticky top-0 z-scrim flex items-center justify-between border-b border-line bg-surface px-4 py-2.5">
          <h1 className="flex items-center gap-2 text-sm font-semibold">
            <Server size={15} /> System Dashboard
          </h1>
          <button
            className="flex items-center gap-1.5 rounded border border-line px-2 py-1 text-xs text-muted hover:bg-hover hover:text-fg"
            onClick={close}
          >
            <ArrowLeft size={13} /> Back to main platform
          </button>
        </header>

        <div className="space-y-3 p-4">
          {!isAdmin && !me.isLoading && (
            <div className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px]">
              <AlertTriangle
                size={13}
                className="mt-0.5 shrink-0 text-amber-500"
              />
              <span>
                This account isn’t a global admin, so every panel below will be
                refused by the server. Nothing here is hidden by the browser —
                the data simply isn’t served to you.
              </span>
            </div>
          )}

          <Panel
            title="Platform"
            icon={<Layers size={12} />}
            state={{ isLoading: counts.isLoading, isError: counts.isError }}
          >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Stat
                label={
                  counts.data?.users_total != null
                    ? `Active users of ${counts.data.users_total}`
                    : "Users"
                }
                value={counts.data?.users}
                icon={<Users size={16} />}
              />
              <Stat
                label="Organizations"
                value={counts.data?.organizations}
                icon={<Building2 size={16} />}
              />
              <Stat
                label="Fields"
                value={counts.data?.fields}
                icon={<Layers size={16} />}
              />
            </div>
          </Panel>

          <div className="grid gap-3 lg:grid-cols-2">
            <Panel
              title="Compute — workers & queue"
              icon={<Server size={12} />}
              state={{
                isLoading: manager.isLoading && jobs.isLoading,
                isError: manager.isError && jobs.isError,
              }}
            >
              <div className="space-y-2 text-[11px]">
                {/*
 BE-Light serves {available:false, reason} until DB grants them SELECT on manager_state --
                  show the REASON, so an ungranted read never reads as a sleeping manager.
*/}
                {manager.data?.available === false && (
                  <p className="rounded border border-line px-2 py-1 text-[10px] text-faint">
                    Manager state unavailable
                    {manager.data.reason ? ` — ${manager.data.reason}` : ""}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Pill
                    on={mgr?.wm_awake ?? undefined}
                    label="Worker manager"
                    unknown={
                      manager.isError || manager.data?.available === false
                    }
                  />
                  <Pill
                    on={mgr?.estimator_awake ?? undefined}
                    label="Estimator"
                    unknown={
                      manager.isError || manager.data?.available === false
                    }
                  />
                  {/*
 The VM count rides IN the manager state (compute stamps it on /reconcile). A stale
                    `as_of` means the GCE look FAILED — rendered distinctly from a real zero.
*/}
                  <span
                    className={`rounded border px-1.5 py-0.5 text-[10px] ${
                      vm.stale
                        ? "border-amber-500/40 text-amber-500"
                        : "border-line text-muted"
                    }`}
                    title={
                      vm.stale
                        ? "Compute could not reach GCE — this is the last count it saw, not a live one"
                        : undefined
                    }
                  >
                    {manager.isError ? "VMs —" : vmLabel(vm)}
                  </span>
                </div>
                {mgr?.capacity_parallel != null && (
                  <div className="text-[10px] text-faint">
                    parallel capacity {mgr.capacity_parallel}
                    {mgr.lease_holder ? ` · lease ${mgr.lease_holder}` : ""}
                    {mgr.estimator_last_tick
                      ? ` · estimator tick ${mgr.estimator_last_tick.slice(11, 16)}`
                      : ""}
                  </div>
                )}
                {jobs.isError ? (
                  <p className="text-faint">Job list unavailable.</p>
                ) : (
                  <p className="text-muted">
                    {activeJobs.length === 0
                      ? "Nothing running or queued."
                      : `${activeJobs.length} active job${activeJobs.length === 1 ? "" : "s"}${jobSummary?.detail ? ` · ${jobSummary.detail}` : ""}`}
                  </p>
                )}
                {blocked.length > 0 && (
                  <p className="text-amber-500">
                    {blocked.length} blocked — waiting on a prereq that may have
                    failed, not live backlog
                  </p>
                )}
                {activeJobs.slice(0, 12).map((j) => (
                  <div
                    key={j.job_id}
                    className="flex items-baseline justify-between gap-2 border-t border-line/50 pt-1"
                  >
                    <span className="truncate text-muted">
                      {j.process_name ?? j.process_id}
                    </span>
                    <span className="shrink-0 text-[10px] text-faint">
                      {j.field_name ?? j.field_id?.slice(0, 8) ?? ""} ·{" "}
                      {j.status === "in_progress"
                        ? "running"
                        : j.queue_position != null
                          ? `#${j.queue_position}`
                          : j.status}
                    </span>
                  </div>
                ))}
                {activeJobs.length > 12 && (
                  <div className="text-[10px] text-faint">
                    +{activeJobs.length - 12} more
                  </div>
                )}
              </div>
            </Panel>

            <UsersPanel enabled={enabled} />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <CapacityPanel enabled={enabled} />
            <ProcessesPanel enabled={enabled} />
          </div>

          <ApiKeysPanel enabled={enabled} />
          <JobsPanel enabled={enabled} />
        </div>
      </div>
    </Modal>
  );
}

/* * Tri-state pill: on / off / unknown — an unreachable source must not read as "off". */
function Pill({
  on,
  label,
  unknown,
}: {
  on?: boolean;
  label: string;
  unknown?: boolean;
}) {
  const cls =
    unknown || on == null
      ? "text-faint"
      : on
        ? "text-emerald-400"
        : "text-red-400";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[10px] ${cls}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${unknown || on == null ? "bg-faint" : on ? "bg-emerald-400" : "bg-red-400"}`}
      />
      {label}
      {unknown || on == null ? " —" : on ? " awake" : " asleep"}
    </span>
  );
}
